import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import api from "../api/axiosInstance";

const ResetPassword = () => {
  const location = useLocation();
  const email = location.state?.email || "";
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Enter OTP, 2: Enter new password
  const navigate = useNavigate();

  const verifyOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.post("/password/verify-reset-otp", {
        email,
        otp,
      });

      if (response.data.success) {
        toast.success("OTP verified!");
        setStep(2);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post("/password/reset", {
        email,
        otp,
        newPassword: password,
        confirmPassword,
      });

      if (response.data.success) {
        toast.success("Password reset successfully!");
        navigate("/login");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Password reset failed");
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      await api.post("/password/forgot-password", null, { params: { email } });
      toast.success("OTP resent to your email!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Error resending OTP");
    }
  };

  return (
    <div className="min-h-screen bg-[#fffdf7] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#fcfaec] rounded-xl p-8 md:p-10 shadow-lg">
        <h1 className="text-3xl font-bold text-black mb-6 text-center">
          Reset Password
        </h1>

        {step === 1 ? (
          <form onSubmit={verifyOtp} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter OTP
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-lime-400 text-center text-2xl font-mono"
                placeholder="000000"
                required
                maxLength={6}
              />
              <p className="text-sm text-gray-500 mt-2">
                Enter the 6-digit OTP sent to {email}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || otp.length !== 6}
              className={`w-full py-3 px-4 rounded-full font-bold text-white ${
                isLoading || otp.length !== 6
                  ? "bg-gray-400"
                  : "bg-lime-500 hover:bg-lime-600"
              } transition duration-200`}
            >
              {isLoading ? "Verifying..." : "Verify OTP"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={resendOtp}
                className="text-lime-600 hover:text-lime-800 font-medium"
              >
                Resend OTP
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-lime-400"
                placeholder="Enter new password"
                required
                minLength="8"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-lime-400"
                placeholder="Confirm new password"
                required
                minLength="8"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-full font-bold text-white ${
                isLoading ? "bg-gray-400" : "bg-lime-500 hover:bg-lime-600"
              } transition duration-200`}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
